<!--
  listing-info-card.vue

  2025-06-04: Added shopping cart functionality to add listings to cart with localStorage
  2025-06-04: Added Agent reserve option with agent checkout modal functionality
  2025-06-03: Added URL parameter sync when date range changes
  2025-05-11: Moved 'Under 3 years old' label under Infants with improved styling
  2024-05-23: Set fixed width for guest picker labels to align count pickers
  2025-04-30: Added back arrow button on mobile top price row to close modal
  2024-06-07: Airbnb price comparison now fetched from /compare-price endpoint
-->
<template>
  <v-card class="listing-info-card rounded-xl my-2" outlined>
    <v-card-title class="d-block" v-if="showBasic">
      <div v-if="currentPricing" class="d-flex align-baseline">
        <v-btn
          v-if="isMobileOrIPad"
          icon
          x-small
          class="me-2 pa-0"
          @click="$emit('close-dialog')"
        >
          <v-icon v-if="isRTL">mdi-chevron-right</v-icon>
          <v-icon v-else>mdi-chevron-left</v-icon>
        </v-btn>
        <span
          class="text-body-1 font-weight-medium mr-2"
          v-text="toDisplayCurrency(dayRate, settings)"
        />
        <!-- <div v-else class="text-body-1 font-weight-medium mr-2">
          {{ toMoney(listing.estimated_day_rate) }}
        </div> -->
        <small class="font-weight-medium">{{ $t("per_night") }}</small>
        <v-btn
          v-if="showInquiry"
          class="justify-end margin-l-auto primary--text text--darken-3"
          text
          small
          @click="showInquiry = !showInquiry"
        >
          <v-icon small class="me-1">mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-btn
        v-if="currentFlow !== 'common'"
        text
        depressed
        x-small
        class="pa-0 mt-6 mt-sm-0"
        @click="currentFlow = 'common'"
      >
        <v-icon color="black" x-small class="me-2" style="width: 5px"
          >$chevron_left</v-icon
        >
        Back
      </v-btn>
      <inquiry-checkout
        v-if="showInquiry"
        :listing="this.listing"
        :currentPricing="this.currentRatePlanPricing"
        @save="saveInquiry"
      />
      <internal-checkout
        v-if="['bank_transfer'].includes(currentFlow)"
        :reservationData="reservationData"
        :currentPricing="this.currentRatePlanPricing"
        :list="this.listing"
        :bankTransferFlow="currentFlow === 'bank_transfer'"
      />
      <div v-else-if="currentFlow === 'bridger_pay'" class="mb-4">
        <div
          class="d-flex justify-center text-h6 font-weight-bold black--text mb-4 mt-2"
        >
          Reservation Confirmation
        </div>
        <div class="font-weight-semibold black--text text-body-2">
          {{ reservationData.listing_nickname }}
        </div>
        <div class="my-3 text-caption black--text font-weight-medium">
          {{ reservationData.description }}
        </div>
      </div>
      <spreedly-express-checkout
        v-if="currentFlow === 'spreedly_express'"
        :key="reservationData.payment_intent_id"
        :reservation-data="reservationData"
        :environment-key="spreedlyEnvironmentKey"
        @payment-loading="isLoading = $event"
      />
      <v-form
        ref="formRule"
        v-if="
          ['common', 'spreedly_express'].includes(currentFlow) && !showInquiry
        "
        class="mb-4"
        @submit.prevent
      >
        <site-date-picker
          :days-rates="listing.days_rates"
          :is-multi-unit="listing.is_multi_unit"
          :value.sync="dateRange"
          :opens="isRTL ? 'right' : 'left'"
          is-range
          is-custom
          :error-text.sync="errorText"
        >
          <div class="d-flex listing-info-card-block pa-2">
            <div class="flex listing-info-card-item mr-4 mr-md-0">
              <p class="text-caption grey--text mb-2">{{ $t("check_in") }}</p>
              <div v-if="dateRange.startDate">
                <div
                  class="d-md-none"
                  v-text="parseDate(dateRange.startDate, 'MMM DD, YYYY')"
                />
                <div class="d-none d-md-block font-weight-medium">
                  <span
                    v-text="parseDate(dateRange.startDate, 'MMM DD, YYYY')"
                  />
                </div>
              </div>
              <span v-else>{{ $t("select_dates") }}</span>
            </div>
            <v-divider class="mx-4" vertical />
            <div class="flex listing-info-card-item">
              <p class="text-caption grey--text mb-2">{{ $t("check_out") }}</p>
              <div v-if="dateRange.endDate">
                <div
                  class="d-md-none"
                  v-text="parseDate(dateRange.endDate, 'MMM DD, YYYY')"
                />
                <div class="d-none d-md-block font-weight-medium">
                  <span v-text="parseDate(dateRange.endDate, 'MMM DD, YYYY')" />
                </div>
              </div>
              <span v-else>{{ $t("select_dates") }}</span>
            </div>
          </div>
        </site-date-picker>
        <div class="error--text text-caption mb-2">{{ errorText }}</div>
        <v-card v-if="acceptBankTransfers" outlined class="my-2">
          <v-card-title
            class="text-body-2 font-weight-semibold mb-0 black--text"
            >{{ $t("select_payment_method") }}</v-card-title
          >
          <v-card-text class="py-0">
            <v-radio-group v-model="paymentMethod" class="mt-0">
              <v-radio label="Credit Card" value="credit_card" color="info">
                <template #label>
                  <span class="text-body-2 black--text">{{
                    $t("credit_card")
                  }}</span>
                </template>
              </v-radio>
              <v-radio label="Bank Transfer" value="bank_transfer" color="info">
                <template #label>
                  <span class="text-body-2 black--text">{{
                    $t("bank_transfer")
                  }}</span>
                </template>
              </v-radio>
            </v-radio-group>
          </v-card-text>
        </v-card>
        <v-row class="px-2 pt-4 pb-8">
          <v-col
            cols="12"
            class="d-flex font-weight-semibold align-center grey--text text--darken-2 pb-0"
          >
            <span class="guest-label">{{ $t("adults") }}</span>
            <count-picker
              :value.sync="adults"
              :max="listing.accommodates - children"
            />
          </v-col>
          <v-col
            cols="12"
            class="d-flex font-weight-semibold align-center grey--text text--darken-2 pb-1"
          >
            <span class="guest-label">{{ $t("children") }}</span>
            <count-picker
              :value.sync="children"
              :min="0"
              :max="listing.accommodates - adults"
            />
          </v-col>
          <v-col
            v-if="settings.show_infants"
            cols="12"
            class="d-flex font-weight-semibold align-center grey--text text--darken-2 pb-0"
          >
            <span class="guest-label">{{ $t("infants") }}</span>
            <count-picker :value.sync="infants" :min="0" />
            <v-icon x-small color="info" class="mx-2">
              mdi-information-outline
            </v-icon>
            <span class="text-caption grey--text">{{
              $t("under_3_years_old")
            }}</span>
          </v-col>
          <v-col
            v-if="showPets"
            cols="12"
            class="d-flex font-weight-semibold align-center grey--text text--darken-2 pb-1"
          >
            <span class="guest-label">{{ $t("pets") }}</span>
            <count-picker :value.sync="pets" :min="0" :max="50" />
          </v-col>
        </v-row>

        <div
          class="text--secondary text-caption w-100 text-end pointer mb-2 text-decoration-underline"
          @click="showCoupon = true"
          v-if="!isAgentReserveSite"
        >
          {{ $t("have_a_coupon") }}
        </div>
        <v-text-field
          v-if="showCoupon && !isAgentReserveSite"
          v-model="couponCode"
          dense
          outlined
          @input="debouncedUpdatePricing"
          :label="$t('coupon_code')"
          :error-messages="
            couponCode && currentRatePlanPricing?.discount === 0
              ? [$t('coupon_code_cannot_be_used')]
              : []
          "
        />

        <div
          v-if="listing.reservation_info"
          class="d-flex listing-info-card-block mb-2 pa-2"
        >
          <div class="flex listing-info-card-item">
            <p class="text-caption grey--text mb-0">{{ $t("guests") }}</p>
            <v-select
              class="px-0"
              :items="guestsAmount"
              v-model="listing.reservation_info.guests_amount"
              dense
              flat
              solo
              hide-details
            />
          </div>
        </div>
        <phone-input
          v-if="isSpreedlyExpress"
          v-model="phone"
          name="phone"
          tabindex="1"
          :label="$t('phone')"
          type="text"
          outlined
          required
          dense
          class="custom-outline mt-8"
        />
        <v-text-field
          v-if="isSpreedlyExpress"
          v-model="email"
          name="email"
          tabindex="1"
          :label="$t('email')"
          type="text"
          outlined
          dense
          class="custom-outline mt4"
          :rules="emailRules"
        />
        <v-select
          v-if="
            hasNonDefaultRatePlans && dateRange.startDate && dateRange.endDate
          "
          v-model="selectedRatePlan"
          :items="ratePlans"
          item-text="name"
          item-value="value"
          :label="$t('rate_plan')"
          outlined
          dense
          class="mb-0"
        />
        <div
          v-if="
            hasNonDefaultRatePlans &&
            !selectedRatePlan &&
            dateRange.startDate &&
            dateRange.endDate
          "
          class="text-caption text-center mt-1 mb-2 d-flex align-center justify-center grey--text"
        >
          <v-icon x-small class="mr-1">mdi-information</v-icon>
          {{ $t("select_preferred_rate_plan") }}
        </div>
        <v-card
          v-if="
            selectedRatePlan &&
            selectedRatePlan !== 'default' &&
            currentRatePlan?.description
          "
          class="mt-1 mb-6 rate-plan-info"
          outlined
          flat
        >
          <v-card-text class="pa-2 text-caption">
            <div
              v-if="
                currentRatePlan.meal_plan &&
                currentRatePlan.meal_plan !== 'default'
              "
              class="d-flex align-center mb-2"
            >
              <v-icon small color="primary" class="mr-1"
                >mdi-silverware-fork-knife</v-icon
              >
              <span class="font-weight-medium text-body-2">{{
                translatedMealPlanMapping[currentRatePlan.meal_plan] ||
                currentRatePlan.meal_plan
              }}</span>
            </div>
            {{ currentRatePlan.description }}
          </v-card-text>
        </v-card>
        <div
          v-if="settings.cancellation_policy"
          class="text-caption grey--text my-2 text-center"
        >
          <v-dialog width="600">
            <template v-slot:activator="{ on }">
              <span v-on="on" class="pointer text-decoration-underline">{{
                $t("cancellation_policy")
              }}</span>
            </template>
            <v-card>
              <v-card-title>{{ $t("cancellation_policy") }}</v-card-title>
              <v-card-text class="whitespace-preline">
                <span>{{ settings.cancellation_policy }}</span>
              </v-card-text>
            </v-card>
          </v-dialog>
        </div>

        <!-- Update the partial charge message with threshold information -->
        <div
          v-if="shouldShowPartialCharge"
          class="text-caption secondary--text font-weight-medium my-4"
        >
          {{
            $t("secure_your_stay", {
              rate: settings.partial_charge_rate,
              threshold:
                settings.partial_reservation_days_threshold &&
                settings.partial_reservation_days_threshold > 0
                  ? $t("days_prior_to_arrival", {
                      days: settings.partial_reservation_days_threshold,
                    })
                  : $t("at_check_in"),
            })
          }}
        </div>

        <v-btn
          v-if="!isAgentReserveSite"
          type="submit"
          class="text-capitalize white--text"
          :color="dark ? 'white' : actionsColor"
          block
          elevation="0"
          large
          @click="submitCheckout"
          :loading="isLoading || pricingLoading"
          :disabled="
            !dateRange.startDate ||
            !dateRange.endDate ||
            !!errorText ||
            pricingLoading ||
            (hasNonDefaultRatePlans && !selectedRatePlan)
          "
        >
          {{ $t("book_now") }}
        </v-btn>

        <!-- Agent Reserve Button for Agent Reserve Site -->
        <v-btn
          v-if="isAgentReserveSite"
          class="text-capitalize white--text"
          :color="dark ? 'white' : actionsColor"
          block
          elevation="0"
          large
          @click="showAgentReserve = true"
          :loading="isLoading || pricingLoading"
          :disabled="
            !dateRange.startDate ||
            !dateRange.endDate ||
            !!errorText ||
            pricingLoading ||
            (hasNonDefaultRatePlans && !selectedRatePlan)
          "
        >
          {{ $t("agent_reserve") }}
        </v-btn>

        <!-- Shopping Cart Button -->
        <v-btn
          v-if="isAgentReserveSite"
          outlined
          block
          class="mt-2"
          :color="actionsColor"
          @click="addToCart"
          :disabled="!dateRange.startDate || !dateRange.endDate"
        >
          <v-icon left small>mdi-cart-plus</v-icon>
          {{ $t("add_to_cart") }}
        </v-btn>

        <div v-if="currentPricing && !isAgentReserveSite" class="w-100 mt-1">
          <div
            v-if="!isAgentReserveSite"
            class="text-caption w-100 text-center pointer mb-2 text-decoration-underline"
            :style="{ color: actionsColor }"
            @click="showInquiry = !showInquiry"
          >
            {{ $t("send_an_inquiry") }}
          </div>
          <div
            v-if="isAgentReserveSite"
            class="text-caption w-100 text-center pointer mb-2 text-decoration-underline"
            :style="{ color: actionsColor }"
            @click="showAgentReserve = true"
          >
            {{ $t("agent_reserve") }}
          </div>
        </div>
      </v-form>
      <div v-if="currentRatePlanPricing">
        <div class="d-flex justify-space-between mb-2">
          <span
            v-text="
              `${toDisplayCurrency(this.dayRate, settings)}
              x
              ${this.currentRatePlanPricing.nights_count} ${$t('nights')}
              `
            "
          ></span>
          <span
            v-text="
              toDisplayCurrency(
                this.dayRate * this.currentRatePlanPricing.nights_count,
                settings
              )
            "
          ></span>
        </div>
        <div
          v-if="currentRatePlanPricing.discount"
          class="d-flex justify-space-between mb-2"
        >
          <span>{{ $t("discount") }}</span>
          <span
            v-text="
              toDisplayCurrency(this.currentRatePlanPricing.discount, settings)
            "
          ></span>
        </div>
        <div
          v-if="currentRatePlanPricing.cleaning_fee"
          class="d-flex justify-space-between mb-2"
        >
          <span>{{ $t("cleaning_fee") }}</span>
          <span
            v-text="
              toDisplayCurrency(
                this.currentRatePlanPricing.cleaning_fee,
                settings
              )
            "
          ></span>
        </div>
        <div
          v-if="
            !settings.show_fees_breakdown && currentRatePlanPricing.taxes !== 0
          "
          class="d-flex justify-space-between mb-2"
        >
          <span v-if="currentRatePlanPricing.vat_inclusive">{{
            $t("service_fee")
          }}</span>
          <span v-else>{{ $t("tax") }}</span>
          <span
            v-text="toDisplayCurrency(currentRatePlanPricing.taxes, settings)"
          ></span>
        </div>
        <template v-if="currentRatePlanPricing.periodic_discount_breakdown">
          <div
            v-for="discount in currentRatePlanPricing.periodic_discount_breakdown"
            :key="discount.title"
            class="d-flex justify-space-between mb-2"
          >
            <span>{{ discount.title }}</span>
            <span v-text="toDisplayCurrency(discount.amount, settings)"></span>
          </div>
        </template>
        <template v-if="settings.show_fees_breakdown">
          <div
            v-for="tax in currentRatePlanPricing.taxes_breakdown"
            :key="tax.name"
            class="d-flex justify-space-between mb-2"
          >
            <span>{{ tax.title }}</span>
            <span v-text="toDisplayCurrency(tax.amount, settings)"></span>
          </div>
        </template>
        <v-divider class="my-4" />
        <price-comparison-card
          v-if="airbnbIsHigher"
          :direct-price="
            toDisplayCurrency(currentRatePlanPricing.total, settings)
          "
          :airbnb-price="airbnbAmount || ''"
          :loading="airbnbLoading"
        />
        <div v-else class="d-flex justify-space-between font-weight-semibold">
          <span
            >{{ $t("total") }}
            <span
              v-if="currentRatePlanPricing.vat_inclusive"
              class="secondary--text text-caption"
              >({{ $t("vat_inc") }}
              {{ currentRatePlanPricing.vat_inclusive.amount }}%)</span
            ></span
          >
          <span
            :class="airbnbAmount ? 'success--text' : ''"
            v-text="toDisplayCurrency(currentRatePlanPricing.total, settings)"
          ></span>
        </div>
        <!-- price comparison -->
        <div
          v-if="
            selectedCurrency &&
            listing &&
            listing.get_currency &&
            selectedCurrency !== listing.get_currency
          "
          class="text-caption warning--text mt-2"
        >
          {{
            $t("note_final_payment", {
              currency: listing.get_currency,
              selectedCurrency: selectedCurrency,
            })
          }}
        </div>
        <div v-if="securityDeposit" class="text-caption grey--text mt-2">
          <span
            >{{
              $t("note_security_deposit", {
                amount: toDisplayCurrency(securityDeposit, settings),
              })
            }}
          </span>
        </div>
      </div>
      <v-btn
        v-if="showWhatsappLink"
        block
        class="mt-4 text-capitalize font-weight-semibold"
        color="success"
        elevation="0"
        large
        :href="whatsappLink"
        target="_blank"
        rel="noopener noreferrer"
      >
        <v-icon class="mx-2">mdi-whatsapp</v-icon>
        {{ $t("contact_via_whatsapp") }}
      </v-btn>

      <!-- Agent Checkout Modal -->
      <agent-checkout
        v-if="isAgentReserveSite"
        :show.sync="showAgentReserve"
        :listing="listing"
        :current-pricing="currentRatePlanPricing"
        :date-range="dateRange"
        @save="saveAgentReservation"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";

import FormattersMixin from "@/mixins/formatters-mixin";
import siteDatePicker from "../common/site-date-picker.vue";
import InternalCheckout from "@/components/listing/internal-checkout.vue";
import InquiryCheckout from "@/components/listing/inquiry-checkout.vue";
import AgentCheckout from "@/components/listing/agent-checkout.vue";
import CountPicker from "@/components/common/count-picker.vue";
import SpreedlyExpressCheckout from "./spreedly-express-checkout.vue";
import FormRules from "@/mixins/form-rules-mixin";
import PhoneInput from "@/components/common/phone-input.vue";
import StylesMixin from "@/mixins/styles-mixin";
import DeviceMixin from "@/mixins/device-mixin";
import PriceComparisonCard from "../common/price-comparison-card.vue";

import { axios } from "@/store/fetch";

export default {
  components: {
    siteDatePicker,
    InternalCheckout,
    InquiryCheckout,
    AgentCheckout,
    CountPicker,
    SpreedlyExpressCheckout,
    PhoneInput,
    PriceComparisonCard,
  },
  name: "listing-info-card",
  mixins: [FormattersMixin, FormRules, StylesMixin, DeviceMixin],
  props: {
    feePercentage: {
      type: Number,

      default: 10,
    },
    dark: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    guestsAmount: [1, 2, 3, 4, 5, 6, 7, 8],
    adults: 2,
    children: 0,
    infants: 0,
    pets: 0,
    couponCode: null,
    dateRange: {
      startDate: null,
      endDate: null,
    },
    mealPlanMapping: {
      default: "Room Only (No Meals)",
      breakfast: "Bed & Breakfast",
      "half-board": "Half Board (Breakfast & Dinner)",
      "full-board": "Full Board (All Meals)",
      "all-inclusive": "All Inclusive",
    },
    isLoading: false,
    errorText: null,
    showCoupon: false,
    currentFlow: "common",
    showSelectPaymentMethod: false,
    paymentMethod: "credit_card",
    showInquiry: false,
    showAgentReserve: false,
    reservationData: null,
    list: null,
    spreedlyEnvironmentKey: null,
    email: null,
    phone: null,
    selectedRatePlan: null,
    airbnbAmount: null, // Airbnb price from API
    airbnbLoading: false,
    airbnbError: null,
  }),
  computed: {
    ...mapState("listingsModule", [
      "currentListing",
      "currentPricing",
      "loading",
      "pricingLoading",
    ]),
    ...mapState({
      settings: (state) => state.settingsModule.settings,
    }),
    isAgentReserveSite() {
      return this.settings.site_type === "agent_reserve";
    },
    isPortfolioSite() {
      return this.isAgentReserveSite && !this.$route.query.agent_id;
    },
    acceptBankTransfers() {
      return this.currentPricing?.accept_bank_transfers;
    },
    ratePlans() {
      const roomOnly = {
        name: this.settings.default_rate_plan_title || this.$t("room_only"),
        value: "default",
      };
      if (!this.currentPricing?.rate_plans?.length) {
        return [roomOnly];
      }

      const plans = this.currentPricing.rate_plans.map((plan) => {
        return {
          name: `${plan.name}`,
          value: plan.id.toString(),
          pricing_offset: plan.pricing_offset,
          meal_plan: plan.meal_plan,
          description: plan.description,
          custom_default: plan.custom_default,
        };
      });

      return [roomOnly, ...plans];
    },
    currentRatePlanPricing() {
      if (!this.currentPricing?.rate_plans_pricings) {
        return null;
      }

      const planId = this.selectedRatePlan || "default";
      return this.currentPricing.rate_plans_pricings[planId];
    },
    currentRatePlan() {
      if (!this.selectedRatePlan || this.selectedRatePlan === "default") {
        return null;
      }
      return this.currentPricing?.rate_plans?.find(
        (plan) => plan.id.toString() === this.selectedRatePlan
      );
    },
    dayRate() {
      if (!this.currentRatePlanPricing) {
        return 0;
      }

      const { day_rate, discount, periodic_discount_value, nights_count } =
        this.currentRatePlanPricing;
      const totalDiscount = discount + periodic_discount_value;

      if (totalDiscount !== null && Math.abs(totalDiscount) > 0) {
        return day_rate + Math.abs(totalDiscount) / nights_count;
      }

      return day_rate;
    },
    isSpreedlyExpress() {
      return this.settings.is_spreedly;
    },
    securityDeposit() {
      const deposit =
        this.currentPricing.security_deposit ||
        this.currentRatePlanPricing?.security_deposit;
      return parseFloat(deposit);
    },
    fee() {
      return (this.listing.price / 100) * this.feePercentage;
    },
    listing() {
      return this.currentListing;
    },
    showBasic() {
      return this.currentFlow === "common" || this.currentFlow === "inquiry";
    },
    showWhatsappLink() {
      return this.settings.whatsapp_number && this.settings.show_whatsapp_link;
    },
    whatsappLink() {
      const phone = this.settings.whatsapp_number;
      let message = `I would like to inquire about ${this.listing.title}`;
      const { startDate, endDate } = this.dateRange || {};
      if (startDate && endDate) {
        message += ` for ${this.parseDate(
          startDate,
          "MMM DD, YYYY"
        )} to ${this.parseDate(endDate, "MMM DD, YYYY")}`;
      }
      return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    },
    showPets() {
      return (
        (this.listing?.amenities?.includes("Pets are welcome") ||
          this.listing?.amenities?.includes("Pets allowed")) &&
        !this.listing?.amenities?.includes("No Pets Allowed")
      );
    },
    hasNonDefaultRatePlans() {
      return this.currentPricing?.rate_plans?.length > 0;
    },
    daysUntilCheckin() {
      if (!this.dateRange.startDate) return 0;
      const today = this.$moment();
      const checkin = this.$moment(this.dateRange.startDate);
      return checkin.diff(today, "days");
    },
    shouldShowPartialCharge() {
      return (
        this.settings.partial_charge_rate &&
        this.currentPricing &&
        (!this.settings.partial_reservation_days_threshold ||
          this.settings.partial_reservation_days_threshold === 0 ||
          this.daysUntilCheckin >=
            this.settings.partial_reservation_days_threshold)
      );
    },
    selectedCurrency() {
      return (
        localStorage.getItem("selectedCurrency") ||
        (this.settings && this.settings.currency) ||
        ""
      );
    },
    hasPeriodicDiscount() {
      return (
        this.currentRatePlanPricing.periodic_discount_breakdown?.length > 0
      );
    },
    translatedMealPlanMapping() {
      return {
        default: this.$t("room_only_no_meals"),
        breakfast: this.$t("bed_and_breakfast"),
        "half-board": this.$t("half_board"),
        "full-board": this.$t("full_board"),
        "all-inclusive": this.$t("all_inclusive"),
      };
    },
    airbnbIsHigher() {
      const numberAmount = Number(this.airbnbAmount?.replace(/[^0-9.-]+/g, ""));
      return numberAmount && numberAmount > this.currentRatePlanPricing.total;
    },
  },
  watch: {
    dateRange(val) {
      if (!val.startDate || !val.endDate) return;
      this.updatePricing();
      this.updateUrlParams();
    },
    adults() {
      if (!this.dateRange.startDate || !this.dateRange.endDate) return;
      this.updatePricing();
      this.updateUrlParams();
    },
    children() {
      if (!this.dateRange.startDate || !this.dateRange.endDate) return;
      this.updatePricing();
      this.updateUrlParams();
    },
    pets() {
      if (!this.dateRange.startDate || !this.dateRange.endDate) return;
      this.updatePricing();
    },
    currentPricing: {
      handler(newPricing) {
        if (newPricing && !this.selectedRatePlan) {
          this.selectedRatePlan =
            this.ratePlans.find((p) => p.custom_default === true)?.value ||
            "default";
        }
      },
      immediate: true,
    },
    selectedRatePlan() {
      if (!this.dateRange.startDate || !this.dateRange.endDate) return;
      this.updatePricing();
    },
  },
  methods: {
    ...mapMutations("listingsModule", ["setCurrentListing"]),
    ...mapMutations("listingsModule", ["setCurrentListing"]),
    ...mapActions("listingsModule", [
      "updateListing",
      "pricingInfo",
      "checkoutSession",
    ]),
    populateFilters() {
      this.dateRange = {
        startDate: this.$route.query.check_in,
        endDate: this.$route.query.check_out,
      };
      this.adults = +this.$route.query.adults || 2;
      this.children = +this.$route.query.children || 0;
    },
    async saveInquiry(inquiry) {
      const { startDate, endDate } = this.dateRange;
      const payload = {
        range: [startDate, endDate],
        full_name: inquiry.full_name,
        phone: inquiry.phone,
        email: inquiry.email,
        fare_accommodation: this.currentRatePlanPricing.fare_accommodation,
        total_taxes: this.currentRatePlanPricing.taxes_n_fees,
        guest_count: null,
        pets_count: this.pets,
        listing_id: this.listing.id,
        note: inquiry.note,
        fare_cleaning: this.currentRatePlanPricing.cleaning_fee,
        tax_lines: this.currentRatePlanPricing.taxes_breakdown,
        fees_items: this.currentRatePlanPricing.fees_breakdown,
        total: this.currentRatePlanPricing.total,
        airbnb_price: this.airbnbAmount,
      };

      try {
        const res = await axios.post("/create-inquiry", {
          ...payload,
        });
        this.isLoading = false;
        if (res && res.status) {
          if (res.status === 200) {
            window.location.href = "/inquiry-success";
          }
        }
      } catch (error) {
        this.isLoading = false;
      }
    },

    async submitCheckout() {
      if (!this.$refs.formRule.validate()) return;
      const { startDate, endDate } = this.dateRange;
      const check_in = this.$moment(startDate).format("YYYY-MM-DD");
      const check_out = this.$moment(endDate).format("YYYY-MM-DD");
      this.isLoading = true;
      try {
        const payload = {
          listingId: this.listing.id,
          coupon_code: this.couponCode,
          city: this.$route.query.city,
          adults: this.adults,
          children: this.children,
          infants: this.infants,
          pets: this.pets,
          check_in,
          check_out,
          email: this.email,
          phone: this.phone,
        };

        if (this.selectedRatePlan && this.selectedRatePlan !== "default") {
          payload.rate_plan = this.selectedRatePlan;
        }
        if (this.paymentMethod === "bank_transfer") {
          this.reservationData = {
            range: [check_in, check_out],
            listing_nickname: this.listing.title,
            description: this.listing.description,
            listing_id: this.listing.id,
          };
          this.currentFlow = "bank_transfer";
          this.isLoading = false;
          return;
        }

        let res;
        try {
          res = await this.checkoutSession(payload);
        } catch (error) {
          this.errorText = error.response.data.error;
          this.isLoading = false;
          return;
        }
        this.isLoading = false;
        if (res && res.data) {
          this.reservationData = res.data;
          if (res.data.tokenizer) {
            this.list = this.listing;
            this.currentFlow = "tokenizer";
            this.$router.push({
              name: "checkout",
              query: {
                flow: "tokenizer",
                booking_event_id: res.data.booking_event_id,
                listing_id: this.listing.id,
                lang: this.$i18n.locale,
              },
            });
          } else if (res.data.spreedly_enviroment_key) {
            this.currentFlow = "spreedly_express";
            this.spreedlyEnvironmentKey = res.data.spreedly_enviroment_key;
          } else if (res.data.bridgerpay_credentials) {
            this.currentFlow = "bridger_pay";
            this.$router.push({
              path: "/checkout",
              query: {
                flow: "bridger_pay",
                booking_event_id: res.data.booking_event_id,
                listing_id: this.listing.id,
                cashier_token: res.data.bridgerpay_credentials.cashier_token,
                payment_intent_id: res.data.payment_intent_id,
                require_address_fields: res.data.require_address_fields,
                cancel_url: res.data.cancel_url,
                lang: this.$i18n.locale,
              },
            });
          } else {
            window.location.href = res.data;
          }
        }

        this.setCurrentListing({ listing: this.listing });
      } catch (error) {
        this.isLoading = false;
      }
    },

    async updatePricing() {
      this.pricingInfo({
        id: this.listing.id,
        params: {
          start_date: this.dateRange.startDate,
          end_date: this.dateRange.endDate,
          coupon_code: this.couponCode,
          adults: this.adults,
          children: this.children,
          pets: this.pets,
          rate_plan_id: this.selectedRatePlan || "default",
        },
      });
      // this.fetchComparePrices();
    },

    async debouncedUpdatePricing() {
      if (this.debouncedInput) {
        clearTimeout(this.debouncedInput);
      }
      this.debouncedInput = setTimeout(() => {
        this.updatePricing();
      }, 500);
    },

    fetchComparePrices() {
      if (!this.settings.compare_prices) return;
      this.airbnbLoading = true;
      this.airbnbError = null;
      const { startDate, endDate } = this.dateRange;
      axios
        .get("/compare-price", {
          params: {
            listing_id: this.listing.id,
            check_in: startDate,
            check_out: endDate,
            adults: this.adults,
            currency: this.selectedCurrency,
          },
        })
        .then(({ data }) => {
          this.airbnbAmount = data.airbnb_price;
          this.airbnbLoading = false;
        })
        .catch((e) => {
          this.airbnbError =
            e?.response?.data?.error || "Error fetching Airbnb price";
          this.airbnbAmount = null;
          this.airbnbLoading = false;
        });
    },

    updateUrlParams() {
      const query = { ...this.$route.query };

      query.check_in = this.parseDate(this.dateRange.startDate, "YYYY-MM-DD");
      query.check_out = this.parseDate(this.dateRange.endDate, "YYYY-MM-DD");

      query.adults = this.adults;
      query.children = this.children;
      const routeQuery = new URLSearchParams(this.$route.query).toString();
      const newQuery = new URLSearchParams(query).toString();
      if (routeQuery == newQuery) return;
      const newPath = `${this.$route.path}?${newQuery}`;
      window.history.replaceState(window.history.state, "", newPath);
    },

    async saveAgentReservation(agentData) {
      try {
        this.isLoading = true;

        const payload = {
          ...agentData,
          range: [agentData.check_in, agentData.check_out],
          fare_accommodation: this.currentRatePlanPricing?.fare_accommodation,
          total_taxes: this.currentRatePlanPricing?.taxes_n_fees,
          guests_count: this.adults + this.children,
          pets_count: this.pets,
          fare_cleaning: this.currentRatePlanPricing?.cleaning_fee,
          tax_lines: this.currentRatePlanPricing?.taxes_breakdown,
          fees_items: this.currentRatePlanPricing?.fees_breakdown,
          total: this.currentRatePlanPricing?.total,
          airbnb_price: this.airbnbAmount,
        };

        const res = await axios.post("/agent-reserve", payload);

        if (res && res.status === 200) {
          this.showAgentReserve = false;
          window.location.href = "/agent-reservation-success";
        }
      } catch (error) {
        console.error("Agent reservation failed:", error);
        // Handle error - could show a notification or error message
      } finally {
        this.isLoading = false;
      }
    },

    addToCart() {
      if (!this.dateRange.startDate || !this.dateRange.endDate) {
        return;
      }
      const cartItem = {
        id: Date.now(), // Unique ID for this cart item
        image:
          this.listing.pictures[0].thumbnail ||
          this.listing.pictures[0].original,
        listing_id: this.listing.id,
        listing_title: this.listing.title,
        check_in: this.parseDate(this.dateRange.startDate, "YYYY-MM-DD"),
        check_out: this.parseDate(this.dateRange.endDate, "YYYY-MM-DD"),
        adults: this.adults,
        children: this.children,
        total_price: this.currentRatePlanPricing?.total,
        currency: this.listing.get_currency,
        added_at: new Date().toISOString(),
      };

      // Get existing cart from localStorage
      const existingCart = JSON.parse(
        localStorage.getItem("shopping_cart") || "[]"
      );

      // Add new item to cart
      existingCart.push(cartItem);

      // Save back to localStorage
      localStorage.setItem("shopping_cart", JSON.stringify(existingCart));

      // Dispatch custom event for immediate cart count update
      window.dispatchEvent(
        new CustomEvent("cartUpdated", {
          detail: { cartItems: existingCart, action: "added", item: cartItem },
        })
      );

      // Show success message (optional - could use a toast notification)
      this.$emit("cart-item-added", cartItem);

      // Could show a success message or update cart count in header
      console.log("Item added to cart:", cartItem);
      this.$router.back();
    },
  },
  mounted() {
    this.populateFilters();
  },
};
</script>

<style lang="scss">
.listing-info-card {
  .margin-l-auto {
    margin-left: auto;
  }
  @media screen and (min-width: 700px) {
    position: sticky;
    top: 100px;
  }

  &-block {
    border-radius: 5px;
    border: 1px solid map-get($grey, "lighten-3");
  }
}

.rate-plan-info {
  background-color: #f5f5f5 !important;
  border: 1px solid #e0e0e0 !important;

  .v-card__text {
    color: rgba(0, 0, 0, 0.6) !important;
    line-height: 1.4;
  }
}

.pointer {
  cursor: pointer;
}
.whitespace-preline {
  white-space: pre-line;
}
.v-text-field--outlined fieldset {
  color: #eeeeee !important;
}
.v-text-field__details {
  margin-bottom: 0px !important;
}
.guest-label {
  display: inline-block;
  min-width: 80px;
  width: 85px;
}
</style>
